import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const ageGroups = ['10-20대', '30-40대', '50-60대', '70대 이상'];
const regions = [
  '서울', '경기', '인천', '강원', '충북', '충남', '대전', '세종',
  '경북', '경남', '대구', '울산', '부산', '전북', '전남', '광주', '제주', '기타',
];

function SignNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center">
              <i className="ri-scales-3-line text-2xl md:text-3xl text-red-600"></i>
            </div>
            <span className={`text-base md:text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              사법정의를 위한 국민연대
            </span>
          </Link>
          <Link
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${isScrolled ? 'text-gray-700 hover:text-red-600' : 'text-white hover:text-red-200'}`}
          >
            <i className="ri-arrow-left-line"></i> 홈으로
          </Link>
        </div>
      </div>
    </nav>
  );
}

function SignFooter() {
  return (
    <footer className="bg-stone-100 text-gray-800 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-scales-3-line text-2xl text-red-600"></i>
            </div>
            <span className="text-base font-bold text-gray-900">사법정의를 위한 국민연대</span>
          </div>
          <p className="text-xs text-gray-400">&copy; 2025 사법정의를 위한 국민연대. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

// 이메일 구독 팝업
function EmailSubscribeModal({ name, onClose }: { name: string; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email.trim()) { setEmailError('이메일을 입력해주세요.'); return; }
    if (!emailPattern.test(email.trim())) { setEmailError('올바른 이메일 형식을 입력해주세요.'); return; }
    setEmailError('');
    setStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('이름', name);
      formBody.append('email', email.trim());
      await fetch('https://readdy.ai/api/form/d6kl6394a3uojp5ffu9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
    } catch {
      // 오류 무시
    }

    setStatus('done');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* 배경 오버레이 */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      {/* 팝업 카드 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-[fadeInUp_0.3s_ease]">
        {/* 상단 배너 */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 pt-7 pb-8 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-28 h-28 bg-white/5 rounded-full -translate-x-10 -translate-y-10"></div>
          <div className="absolute bottom-0 right-0 w-36 h-36 bg-white/5 rounded-full translate-x-14 translate-y-14"></div>
          <div className="relative z-10 w-14 h-14 flex items-center justify-center bg-white rounded-full mx-auto mb-3 shadow-md">
            <i className="ri-mail-send-line text-2xl text-red-600"></i>
          </div>
          <h3 className="relative z-10 text-xl font-black text-white mb-1" style={{ fontFamily: "'Nanum Myeongjo', serif" }}>
            소식 이메일로 받기
          </h3>
          <p className="relative z-10 text-red-100 text-xs leading-relaxed">
            사법정의 국민연대의 최신 소식을<br />이메일로 받아보세요
          </p>
        </div>

        {/* 본문 */}
        <div className="px-6 py-6">
          {status === 'done' ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
                <i className="ri-check-line text-2xl text-green-600"></i>
              </div>
              <p className="text-base font-bold text-gray-900 mb-1">구독 신청 완료!</p>
              <p className="text-sm text-gray-500 mb-5">소중한 관심 감사합니다. 최신 소식을 전달해 드리겠습니다.</p>
              <button
                onClick={onClose}
                className="w-full bg-red-600 text-white py-3 rounded-lg font-bold text-sm hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                확인
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                <span className="font-semibold text-gray-900">{name}</span>님, 서명에 참여해 주셔서 감사합니다!<br />
                이메일을 등록하시면 사법정의 운동의 진행 상황과 중요 소식을 받아보실 수 있습니다.
              </p>
              <form onSubmit={handleSubmit} data-readdy-form id="email-subscribe-form">
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    이메일 주소
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${emailError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                    placeholder="example@email.com"
                    autoFocus
                  />
                  {emailError && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>{emailError}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-bold text-sm hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 mb-3"
                >
                  {status === 'submitting' ? (
                    <><i className="ri-loader-4-line animate-spin"></i> 신청 중...</>
                  ) : (
                    <><i className="ri-mail-send-line"></i> 소식 받기</>
                  )}
                </button>
              </form>
              <button
                onClick={onClose}
                className="w-full text-gray-400 text-sm py-2 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                괜찮습니다, 다음에 할게요
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SuccessScreen({ name }: { name: string }) {
  const [copied, setCopied] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(true);
  const siteUrl = window.location.origin + '/sign';
  const shareText = '사법장악 독재 3법을 즉각 철회하라! 사법정의를 위한 범국민 서명 운동에 동참해주세요.';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(siteUrl);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = siteUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {showEmailModal && (
        <EmailSubscribeModal name={name} onClose={() => setShowEmailModal(false)} />
      )}
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
          <div className="relative bg-gradient-to-br from-red-500 via-red-600 to-red-700 px-8 pt-10 pb-14 text-center overflow-hidden">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white/5 rounded-full -translate-x-12 -translate-y-12"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/5 rounded-full translate-x-16 translate-y-16"></div>
            <div className="relative z-10 mb-4">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-lg animate-bounce">
                <i className="ri-check-line text-4xl text-red-600"></i>
              </div>
            </div>
            <h2 className="relative z-10 text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Nanum Myeongjo', serif" }}>
              서명이 완료되었습니다!
            </h2>
            <p className="relative z-10 text-red-100 text-sm">사법정의를 위한 소중한 한 걸음에 감사드립니다</p>
          </div>

          <div className="px-8 py-8">
            <div className="text-center mb-6">
              <p className="text-lg font-semibold text-gray-900 mb-1">
                <span className="text-red-600">{name}</span>님, 감사합니다!
              </p>
              <p className="text-sm text-gray-500 leading-relaxed">귀하의 서명이 사법정의 회복을 위한 큰 힘이 됩니다.</p>
            </div>

            <div className="mb-6">
              <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <i className="ri-share-forward-line text-red-600"></i> 서명 참여를 널리 알려주세요!
              </p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(siteUrl)}`, '_blank', 'noopener,noreferrer')}
                  className="flex flex-col items-center gap-1.5 bg-gray-50 hover:bg-gray-100 rounded-xl py-3 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-900 rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-twitter-x-fill text-lg text-white"></i>
                  </div>
                  <span className="text-xs font-semibold text-gray-700">X</span>
                </button>
                <button
                  onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}`, '_blank', 'noopener,noreferrer')}
                  className="flex flex-col items-center gap-1.5 bg-blue-50 hover:bg-blue-100 rounded-xl py-3 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-600 rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-facebook-fill text-lg text-white"></i>
                  </div>
                  <span className="text-xs font-semibold text-gray-700">페이스북</span>
                </button>
                <button
                  onClick={handleCopyLink}
                  className="flex flex-col items-center gap-1.5 bg-gray-50 hover:bg-gray-100 rounded-xl py-3 transition-colors cursor-pointer group"
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full group-hover:scale-110 transition-all ${copied ? 'bg-green-500' : 'bg-gray-500'}`}>
                    <i className={`${copied ? 'ri-check-line' : 'ri-link'} text-lg text-white`}></i>
                  </div>
                  <span className="text-xs font-semibold text-gray-700">{copied ? '복사됨!' : '링크복사'}</span>
                </button>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="bg-teal-50 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg shrink-0">
                    <i className="ri-government-line text-sm text-teal-600"></i>
                  </div>
                  <div>
                    <a
                      href="https://petitions.assembly.go.kr/proceed/registered/48DF3A32E4373F31E064ECE7A7064E8B"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-sm font-semibold text-teal-700 hover:text-teal-900 underline underline-offset-2 cursor-pointer transition-colors"
                    >
                      국회 청원도 함께해주세요
                    </a>
                    <p className="text-xs text-gray-500 mt-0.5">국민동의청원에도 참여하여 목소리를 높여주세요</p>
                  </div>
                </div>
                <a
                  href="https://petitions.assembly.go.kr/proceed/registered/48DF3A32E4373F31E064ECE7A7064E8B"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-full flex items-center justify-center bg-teal-600 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-government-line mr-2"></i> 국회 국민동의청원 바로가기
                </a>
              </div>
            </div>

            <div className="mb-3">
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-3 text-center">
                <p className="text-sm font-bold text-red-700 leading-relaxed">
                  Freedom is not free!<br />
                  <span className="text-gray-800 font-semibold">자유 대한민국을 위해 함께 기도하고 행동하실 분들을 초대합니다!</span>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <a href="https://t.me/+1WdQdYGXQFU2NmE1" target="_blank" rel="noopener noreferrer nofollow"
                  className="w-full bg-blue-500 text-white py-3 rounded-xl text-sm font-semibold hover:bg-blue-600 transition-colors text-center cursor-pointer whitespace-nowrap">
                  <i className="ri-telegram-line mr-1.5"></i> 텔레그램
                </a>
                <a href="https://open.kakao.com/o/gE2hVphi" target="_blank" rel="noopener noreferrer nofollow"
                  className="w-full bg-yellow-400 text-gray-900 py-3 rounded-xl text-sm font-semibold hover:bg-yellow-500 transition-colors text-center cursor-pointer whitespace-nowrap">
                  <i className="ri-chat-3-line mr-1.5"></i> 카카오톡
                </a>
              </div>
            </div>
            <Link to="/" className="block w-full bg-gray-100 text-gray-700 py-3 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors text-center cursor-pointer whitespace-nowrap">
              홈으로 돌아가기
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default function SignPage() {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'duplicate'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [signerCount, setSignerCount] = useState<number | null>(null);
  const [petitionCount, setPetitionCount] = useState<number>(0);
  const [expirDt, setExpirDt] = useState<string>('');
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    ageGroup: '',
    region: '',
    overseasCountry: '',
    agreePrivacy: false,
    agreeDisplay: false,
    comment: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [privacyError, setPrivacyError] = useState('');

  useEffect(() => {
    // 범국민 서명 수
    supabase.from('signers').select('id', { count: 'exact', head: true }).then(({ count }) => {
      if (count !== null) setSignerCount(count);
    });

    // 국회 청원 수
    const fetchPetition = async () => {
      try {
        const petitId = import.meta.env.VITE_PETIT_ID;
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
        if (!supabaseUrl || !supabaseAnonKey || !petitId) return;
        const res = await fetch(
          `${supabaseUrl}/functions/v1/petition-crawler?petitId=${petitId}`,
          {
            headers: {
              Authorization: `Bearer ${supabaseAnonKey}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data.success && data.agreCo !== null && data.agreCo !== undefined) {
          setPetitionCount(Number(data.agreCo));
        }
        if (data.expirDt) setExpirDt(data.expirDt);
      } catch {
        // ignore
      }
    };
    fetchPetition();
  }, []);

  const maskName = (name: string) => {
    if (name.length <= 1) return name;
    if (name.length === 2) return name[0] + 'ㅇ';
    return name[0] + 'ㅇ'.repeat(name.length - 1);
  };

  const getDisplayName = () => {
    if (!formData.fullName) return '';
    return formData.agreeDisplay ? formData.fullName : maskName(formData.fullName);
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    const namePattern = /^[가-힣a-zA-Z\s]+$/;
    if (!formData.fullName.trim()) errs.fullName = '이름을 입력해주세요.';
    else if (!namePattern.test(formData.fullName.trim())) errs.fullName = '이름은 한글 또는 영문만 입력 가능합니다.';
    else if (formData.fullName.trim().length < 2) errs.fullName = '이름은 최소 2자 이상 입력해주세요.';

    if (formData.phone.trim()) {
      const phonePattern = /^01[016789]-?\d{3,4}-?\d{4}$/;
      if (!phonePattern.test(formData.phone.trim())) errs.phone = '올바른 전화번호 형식을 입력해주세요. (예: 010-1234-5678)';
    }

    if (!formData.ageGroup) errs.ageGroup = '연령대를 선택해주세요.';
    if (!formData.region) errs.region = '지역을 선택해주세요.';
    if (formData.region === '해외' && !formData.overseasCountry.trim()) errs.overseasCountry = '국가명을 입력해주세요.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!formData.agreePrivacy) {
      setPrivacyError('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setDuplicateMessage('');

    const finalRegion = formData.region === '해외'
      ? `해외 (${formData.overseasCountry.trim()})`
      : formData.region;

    try {
      if (formData.phone.trim()) {
        const { data: existingByPhone } = await supabase
          .from('signers').select('id').eq('phone', formData.phone.trim()).eq('name', formData.fullName.trim()).limit(1);
        if (existingByPhone && existingByPhone.length > 0) {
          setSubmitStatus('duplicate');
          setDuplicateMessage('이미 동일한 이름과 전화번호로 서명하셨습니다.');
          setIsSubmitting(false);
          return;
        }
      }

      const { error: signerError } = await supabase
        .from('signers')
        .insert([{
          name: formData.fullName.trim(),
          email: '',
          phone: formData.phone.trim(),
          country: formData.region === '해외' ? formData.overseasCountry.trim() : '대한민국',
          city: finalRegion,
          age_group: formData.ageGroup,
          region: finalRegion,
          hide_name: !formData.agreeDisplay,
          agree_privacy: formData.agreePrivacy,
          agree_display: formData.agreeDisplay,
          comment: formData.comment,
          is_signature: true,
          likes: 0,
          user_id: null,
        }])
        .select()
        .maybeSingle();

      if (signerError) {
        if (signerError.code === '23505') {
          setSubmitStatus('duplicate');
          setDuplicateMessage('이미 서명에 참여하셨습니다. 중복 서명은 불가합니다.');
          setIsSubmitting(false);
          return;
        }
        throw signerError;
      }

      if (formData.comment.trim()) {
        const displayName = getDisplayName() || formData.fullName.trim();
        await supabase.from('comments').insert([{ name: displayName, message: formData.comment.trim(), likes: 0, is_best: false, user_id: null }]);
      }

      setSubmitStatus('success');
    } catch (error) {
      console.error('Error submitting signature:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <>
        <SignNavbar />
        <SuccessScreen name={formData.fullName} />
        <SignFooter />
      </>
    );
  }

  const PETITION_TARGET = 50000;
  const SIGNATURE_TARGET = 10000000;
  const currentSignature = signerCount ?? 0;
  const petitionPct = Math.min((petitionCount / PETITION_TARGET) * 100, 100);
  const signaturePct = Math.min((currentSignature / SIGNATURE_TARGET) * 100, 100);
  const totalCount = petitionCount + currentSignature;

  const formatExpirDt = (dt: string) => {
    if (!dt) return '';
    if (dt.length === 8) return `${dt.slice(0, 4)}.${dt.slice(4, 6)}.${dt.slice(6, 8)}`;
    return dt;
  };

  return (
    <>
      <SignNavbar />

      {/* 히어로 */}
      <div className="relative w-full h-64 md:h-80 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/ab238e399a4bb6fe8b2618467c04fb37.jpg"
          alt="서명 참여하기 배경"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-3xl md:text-5xl font-black text-white mb-3" style={{ fontFamily: "'Nanum Myeongjo', serif" }}>
            서명 참여하기
          </h1>
          <p className="text-white/80 text-sm md:text-base max-w-xl">
            사법정의를 위한 청년 120인 선언에 동참해주세요
          </p>
        </div>
      </div>

      {/* 참여 현황 - A/B 분리 + 통합 카운터 */}
      <div className="bg-white border-b border-gray-100 py-8 px-4 shadow-sm">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 text-center">실시간 참여 현황</p>

          {/* A/B 카드 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">

            {/* A. 국회 국민동의 청원 */}
            <div className="border border-red-100 rounded-2xl p-4 bg-red-50/50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center bg-red-100 rounded-lg shrink-0">
                  <i className="ri-government-line text-sm text-red-600"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800">국회 국민동의 청원</p>
                  <p className="text-[10px] text-gray-400">목표: {PETITION_TARGET.toLocaleString()}명</p>
                </div>
              </div>
              <div className="flex items-end justify-between mb-2">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-red-600 tabular-nums leading-none">
                    {petitionCount.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-400">명</span>
                </div>
                <span className="text-xs font-bold text-red-500">{petitionPct.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-red-100 rounded-full h-3 overflow-hidden mb-2">
                <div
                  className="bg-gradient-to-r from-red-500 to-rose-500 h-3 rounded-full transition-all duration-700"
                  style={{ width: `${Math.max(petitionPct, 0.3)}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between">
                {expirDt ? (
                  <p className="text-[10px] text-gray-400 flex items-center gap-1">
                    <i className="ri-calendar-line"></i>마감: {formatExpirDt(expirDt)}
                  </p>
                ) : (
                  <span></span>
                )}
                <a
                  href="https://petitions.assembly.go.kr/proceed/registered/48DF3A32E4373F31E064ECE7A7064E8B"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-[10px] text-red-600 hover:text-red-700 font-semibold flex items-center gap-0.5 cursor-pointer whitespace-nowrap"
                >
                  청원 참여 <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>

            {/* B. 범국민 서명 운동 */}
            <div className="border border-teal-100 rounded-2xl p-4 bg-teal-50/50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-lg shrink-0">
                  <i className="ri-quill-pen-line text-sm text-teal-600"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800">범국민 서명 운동</p>
                  <p className="text-[10px] text-gray-400">목표: {SIGNATURE_TARGET.toLocaleString()}명</p>
                </div>
              </div>
              <div className="flex items-end justify-between mb-2">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-teal-600 tabular-nums leading-none">
                    {currentSignature.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-400">명</span>
                </div>
                <span className="text-xs font-bold text-teal-500">{signaturePct.toFixed(2)}%</span>
              </div>
              <div className="w-full bg-teal-100 rounded-full h-3 overflow-hidden mb-2">
                <div
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 h-3 rounded-full transition-all duration-700"
                  style={{ width: `${Math.max(signaturePct, 0.3)}%` }}
                ></div>
              </div>
              <p className="text-[10px] text-gray-400 flex items-center gap-1">
                <i className="ri-fire-line text-teal-500"></i>
                지금도 많은 분들이 서명 중입니다
              </p>
            </div>
          </div>

          {/* 통합 카운터 */}
          <div className="bg-gradient-to-r from-red-600 to-rose-700 rounded-2xl px-5 py-4 flex items-center justify-between gap-4 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
                <i className="ri-fire-line text-lg text-white"></i>
              </div>
              <div>
                <p className="text-xs text-red-100 font-semibold">청원 + 서명 통합 참여자</p>
                <p className="text-[10px] text-red-200">사법정의를 위해 함께하는 목소리</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="flex items-baseline gap-1 justify-end">
                <span className="text-3xl font-black text-white tabular-nums leading-none">
                  {totalCount.toLocaleString()}
                </span>
                <span className="text-sm text-red-200 font-bold">명</span>
              </div>
              <p className="text-[10px] text-red-200 mt-0.5">총 참여자</p>
            </div>
          </div>
        </div>
      </div>

      {/* 서명 안내 */}
      <div className="bg-amber-50 border-b border-amber-100 py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-start gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-amber-500 rounded-full shrink-0 mt-0.5">
            <i className="ri-information-line text-white text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-amber-900 mb-1">서명 안내</p>
            <p className="text-xs text-amber-800 leading-relaxed">
              아래 정보를 정확히 입력해주세요. 입력하신 개인정보는 서명 운동 목적으로만 사용되며, 운동 종료 후 즉시 파기됩니다. 제3자에게 제공되지 않습니다.
            </p>
            <p className="text-xs text-red-700 font-semibold mt-1">
              ※ 1인 1회 서명만 가능합니다. 동일한 전화번호로 중복 서명은 불가합니다.
            </p>
          </div>
        </div>
      </div>

      {/* 폼 영역 */}
      <div className="bg-gray-50 min-h-screen py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 space-y-6"
          >
            {/* 이름 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                이름 <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="이름"
                value={formData.fullName}
                onChange={(e) => { setFormData({ ...formData, fullName: e.target.value }); if (errors.fullName) setErrors({ ...errors, fullName: '' }); }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${errors.fullName ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                placeholder="홍길동 또는 Hong Gildong"
                maxLength={50}
              />
              {errors.fullName && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.fullName}</p>}
              <p className="text-xs text-gray-500 mt-1"><i className="ri-information-line mr-1"></i>한글 또는 영문만 입력 가능합니다</p>
            </div>

            {/* 전화번호 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                연락처 <span className="text-red-600">*</span> <span className="text-xs text-gray-400 font-normal">(중복 서명 방지용)</span>
              </label>
              <input
                type="tel"
                name="전화번호"
                value={formData.phone}
                onChange={(e) => { setFormData({ ...formData, phone: e.target.value }); if (errors.phone) setErrors({ ...errors, phone: '' }); }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${errors.phone ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                placeholder="010-1234-5678"
                maxLength={13}
              />
              {errors.phone && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.phone}</p>}
            </div>

            {/* 지역 + 연령대 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  지역 <span className="text-red-600">*</span>
                </label>
                <select
                  name="지역"
                  value={formData.region}
                  onChange={(e) => { setFormData({ ...formData, region: e.target.value, overseasCountry: '' }); if (errors.region) setErrors({ ...errors, region: '', overseasCountry: '' }); }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${errors.region ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                >
                  <option value="">거주 지역 선택</option>
                  {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                  <option value="해외">해외</option>
                </select>
                {errors.region && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.region}</p>}
                {formData.region === '해외' && (
                  <div className="mt-2">
                    <input
                      type="text"
                      name="국가명"
                      value={formData.overseasCountry}
                      onChange={(e) => { setFormData({ ...formData, overseasCountry: e.target.value }); if (errors.overseasCountry) setErrors({ ...errors, overseasCountry: '' }); }}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${errors.overseasCountry ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                      placeholder="국가명을 입력해주세요 (예: 미국, 캐나다)"
                      maxLength={50}
                    />
                    {errors.overseasCountry && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.overseasCountry}</p>}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  연령대 <span className="text-red-600">*</span>
                </label>
                <select
                  name="연령대"
                  value={formData.ageGroup}
                  onChange={(e) => { setFormData({ ...formData, ageGroup: e.target.value }); if (errors.ageGroup) setErrors({ ...errors, ageGroup: '' }); }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${errors.ageGroup ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                >
                  <option value="">선택하세요</option>
                  {ageGroups.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                {errors.ageGroup && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.ageGroup}</p>}
              </div>
            </div>

            {/* 의견 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">
                  의견 <span className="text-xs text-gray-400 font-normal">(선택)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none group">
                  <input
                    type="checkbox"
                    checked={formData.agreeDisplay}
                    onChange={(e) => setFormData({ ...formData, agreeDisplay: e.target.checked })}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-2 focus:border-transparent cursor-pointer flex-shrink-0"
                  />
                  <span className="text-xs font-medium text-gray-600 group-hover:text-gray-900 transition-colors">
                    실명 공개
                    <span className="text-gray-400 font-normal ml-1">(선택)</span>
                  </span>
                </label>
              </div>
              <textarea
                name="응원메시지"
                value={formData.comment}
                onChange={(e) => {
                  if (e.target.value.length <= 500) setFormData({ ...formData, comment: e.target.value });
                }}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
                placeholder="사법정의 회복을 위한 응원의 메시지를 남겨주세요 (최대 500자)"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  홈 화면 표시명:&nbsp;
                  <span className={`font-semibold ${formData.agreeDisplay ? 'text-gray-800' : 'text-gray-500'}`}>
                    {formData.fullName
                      ? getDisplayName()
                      : <span className="text-gray-400">{formData.agreeDisplay ? '홍길동' : '홍ㅇㅇ'}</span>
                    }
                  </span>
                  <span className="text-gray-400 ml-1">
                    ({formData.agreeDisplay ? '실명' : '비공개'})
                  </span>
                </p>
                <p className="text-xs text-gray-400">{formData.comment.length}/500</p>
              </div>
            </div>

            {/* 개인정보 동의 */}
            <div className="bg-gray-50 rounded-xl p-5 space-y-2">
              <p className="text-sm font-bold text-gray-800 mb-3">개인정보 수집 및 이용 동의</p>
              <p className="text-xs text-gray-600 leading-relaxed">
                1. 수집 항목: 이름, 연락처, 지역<br />
                2. 수집 목적: 서명 운동 참여 확인 및 통계<br />
                3. 보유 기간: 운동 종료 후 즉시 파기<br />
                4. 제3자 제공: 없음<br />
                5. 동의 거부 권리: 개인정보 수집 및 이용에 대한 동의를 거부할 수 있으며, 이 경우 서명 참여가 제한됩니다.
              </p>
              <label className="flex items-start space-x-3 cursor-pointer pt-2">
                <input
                  type="checkbox"
                  checked={formData.agreePrivacy}
                  onChange={(e) => { setFormData({ ...formData, agreePrivacy: e.target.checked }); if (e.target.checked) setPrivacyError(''); }}
                  className="mt-1 w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 cursor-pointer flex-shrink-0"
                />
                <span className="text-sm font-semibold text-gray-900">
                  위 내용을 확인하였으며, 개인정보 수집 및 이용에 동의합니다. <span className="text-red-600">*</span>
                </span>
              </label>
              {privacyError && <p className="text-xs text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i>{privacyError}</p>}
            </div>

            {submitStatus === 'duplicate' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-error-warning-fill text-2xl text-amber-600 flex-shrink-0"></i>
                <div>
                  <p className="text-sm font-semibold text-amber-900">중복 서명 안내</p>
                  <p className="text-xs text-amber-700">{duplicateMessage}</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-error-warning-fill text-2xl text-red-600 flex-shrink-0"></i>
                <div>
                  <p className="text-sm font-semibold text-red-900">서명 중 오류가 발생했습니다</p>
                  <p className="text-xs text-red-700">다시 시도해주세요.</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !formData.agreePrivacy}
              className="w-full bg-red-600 text-white py-3.5 rounded-lg font-bold text-base hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <><i className="ri-loader-4-line animate-spin"></i> 제출 중...</>
              ) : (
                <><i className="ri-edit-line"></i> 서명 제출하기</>
              )}
            </button>
          </form>

          {/* 주변에 알려주세요 */}
          <div className="mt-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-3">
              <i className="ri-megaphone-line text-amber-600 text-xl"></i>
            </div>
            <p className="text-sm font-bold text-gray-800 mb-1">주변에 알려주세요</p>
            <p className="text-xs text-gray-500 mb-4">더 많은 분들의 참여를 위해 공유해주세요</p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.origin + '/sign').catch(() => {});
              }}
              className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-links-line"></i> 링크 복사
            </button>
          </div>
        </div>
      </div>

      <SignFooter />
    </>
  );
}
